<template lang="pug">
.talkToUs
  template(v-if="!isSubmitted")

    .talkToUs__section
      slot

    .talkToUs__section

      //-
      //- Contact form
      //-
      form.contact__form(@submit.prevent="handleSubmit")
        .contact__form__input
          label.contact__form__label(for="contact-email")
            | {{ $t('contact.email') }}
            span.visually-hidden(v-if="errors.email || errors.emailValid") (required)

          input(
            id="contact-email"
            v-model="contact.email"
            type="email"
            name="email"
            autocomplete="email"
            :placeholder="$t('contact.email')"
            :aria-invalid="errors.email || errors.emailValid || undefined"
            :aria-describedby="(errors.email || errors.emailValid) ? 'email-error' : undefined"
            aria-required="true")

        .contact__form__action
          button.button(
            type="submit"
            :disabled="isSubmitting") {{ isSubmitting ? $t('contact.sending') : $t('contact.send') }}

      .contact__form__error(
        v-if="errors.email || errors.emailValid"
        id="email-error"
        role="alert"
        aria-live="polite")
        span(v-if="errors.email") {{ $t('contact.required') }}
        span(v-if="errors.emailValid") {{ $t('contact.invalidEmail') }}

      .contact__form__error.contact__form__error--submission(
        v-if="hasSubmissionError"
        role="alert"
        aria-live="assertive") {{ $t('contact.submitError') }}

  //-
  //- Contact submitted View
  //-
  .contact__submitted(v-else)
    h4.gradient.margin-bottom-half {{ $t('contact.thankYou') }}
    p.font-size-base.no-margin-bottom {{ $t('contact.getBack') }}
</template>

<script setup lang="ts">
  import { reactive, shallowRef } from 'vue'
  import type { FinancialInstrument, IssuanceType } from '../types'

  export interface TalkToUsProps {
    /** Selected financial instrument for the simulator request. */
    financialInstrument: FinancialInstrument
    /** Selected issuance type for the simulator request. */
    issuanceType: IssuanceType
    /** Simulated amount included in the contact request. */
    amount: number
  }

  //
  // Props and events
  //
  const props = defineProps<TalkToUsProps>()

  //
  // Data
  //
  const isSubmitted = shallowRef(false)
  const isSubmitting = shallowRef(false)
  const hasSubmissionError = shallowRef(false)
  const contact = reactive({
    email: '',
  })
  const errors = reactive({
    email: false,
    emailValid: false,
  })

  //
  // Methods
  //
  function validateEmail(email: string): boolean {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  function validateRequired(value: string): boolean {
    return value.length > 0
  }

  async function handleSubmit(event: Event) {
    event.preventDefault()

    if (isSubmitting.value) return

    // Reset errors
    errors.email = false
    errors.emailValid = false
    hasSubmissionError.value = false

    // Validate form
    if (!validateRequired(contact.email)) errors.email = true
    if (!validateEmail(contact.email)) errors.emailValid = true

    if (Object.values(errors).some(error => error)) return

    isSubmitting.value = true

    try {
      await $fetch('/api/simulator-contact', {
        method: 'POST',
        body: JSON.stringify({
          email: contact.email,
          financialInstrument: props.financialInstrument,
          issuanceType: props.issuanceType,
          amount: props.amount,
        }),
      })

      isSubmitted.value = true

      // Reset form
      contact.email = ''
    } catch {
      hasSubmissionError.value = true
    } finally {
      isSubmitting.value = false
    }
  }
</script>

<style lang="scss" scoped>
  .talkToUs {
    margin: 0 auto;
    text-align: left;
    display: flex;
    justify-content: center;
    gap: simple();
    flex-direction: column;
    padding: 0 simple();
  }

  .contact__form {
    display: flex;
    gap: half();
    align-items: flex-end;
  }

  .contact__form__input {
    width: 100%;

    input {
      width: 100%;
      border-radius: $border-radius;
      border-color: color(gray7);
      margin-bottom: 0;

      &[aria-invalid='true'] {
        border-color: color(danger);
      }
    }
  }

  .contact__form__label {
    display: block;
    font-size: font-size(small1);
    font-weight: font-weight(semibold);
    color: color(gray-dark);
    margin-bottom: quarter();
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .contact__form__error {
    color: color(danger);
    font-size: font-size(small1);
    padding-left: half();
    margin-top: quarter();
    display: flex;
    flex-direction: column;
    gap: quarter();

    span {
      display: block;
    }
  }

  .contact__submitted {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  @include media(palm) {
    .contact__submitted {
      padding: double() simple();
      max-width: inherit;
      min-width: inherit;
    }

    .contact__form {
      flex-direction: column;
      align-items: center;
    }

    .contact__form__action {
      width: 100%;

      .button {
        width: 100%;
      }
    }
  }
</style>
